import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectFeatures } from 'app/pages/Home/slice/selectors';
import { MomentData, MilestonesGridData } from '../types';

/**
 * Custom hook to filter milestones grid data by Surfaces (Features)
 * Uses exact matching logic for surfaces
 * Filters on: moment.filters.surfaces (array field)
 */
export const useFilterBySurfaces = (
  milestonesData: MilestonesGridData | null
): MilestonesGridData | null => {
  // Get selected surfaces/features from Redux state
  const features = useSelector(selectFeatures);
  const selectedSurfaces = features?.selectedFeatures || [];

  // Memoize the filtered data to avoid unnecessary recalculations
  const filteredData = useMemo(() => {
    // If no data, return null
    if (!milestonesData || !milestonesData.data) {
      return null;
    }

    // If no surface filters are selected, return original data
    if (selectedSurfaces.length === 0) {
      return milestonesData;
    }

    // Filter the data based on selected surfaces
    const filtered: MomentData[] = milestonesData.data.filter(moment => {
      const momentSurfaces = moment.filters?.surfaces;

      // If surface filter is active but moment has no surfaces, exclude it
      if (
        !momentSurfaces ||
        !Array.isArray(momentSurfaces) ||
        momentSurfaces.length === 0
      ) {
        return false;
      }

      // Check if any of the moment's surfaces matches any selected surface (exact match)
      return momentSurfaces.some(surface => selectedSurfaces.includes(surface));
    });

    return {
      ...milestonesData,
      data: filtered
    };
  }, [milestonesData, selectedSurfaces]);

  return filteredData;
};
